import React from "react";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { useMountEffect } from "../../hooks/useMountEffect";
import { IChoiceField } from "../../models/formFields";
import { FormInputFieldWrapper } from "../FormInputFieldWrapper";
import { useFormField } from "../hooks/useFormField";

const Option = (props: { label: string; value: string }): React.JSX.Element => (
    <option value={props.value}>{props.label}</option>
);

export const FormSelect = <T extends string>(
    props: IChoiceField<T>,
): React.ReactNode => {
    const { type = "choice", choices } = props;

    const field = useFormField<T, HTMLSelectElement>({
        ...props,
        type: type,
    });

    /**
     * This exists strictly as an IE Edge bug workaround. Done on 10 Sept 2016
     * See https://github.com/facebook/react/issues/7655 and
     * https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/8794503/
     * It fixes a bug where the Wells Fargo application wasn't taking the State value
     * into its select box, and it was throwing a validation error.
     */
    useMountEffect(() => {
        let selectedIndex = -1;
        const input = field.inputRef.current;
        if (input) {
            if (
                typeof input !== "undefined" &&
                typeof input.selectedIndex !== "undefined"
            ) {
                selectedIndex = input.selectedIndex;
            }

            if (selectedIndex >= 0) {
                let tempOption: HTMLOptionElement | null = null;
                if (input.childNodes.length === 1) {
                    const document = getDocument();
                    if (document) {
                        tempOption = document.createElement("option");
                        input.appendChild(tempOption);
                    }
                }

                const options = input.options;
                const tempIndex = (selectedIndex + 1) % options.length;
                options.item(tempIndex)!.selected = true;
                options.item(selectedIndex)!.selected = true;

                if (tempOption) {
                    input.removeChild(tempOption);
                }
            }
        }
    });

    const options = (choices || []).map((option) => (
        <Option key={option.value} {...option} />
    ));

    const selectProps = {
        ...field.inputProps,
        id: field.inputProps.id ?? props.id,
    };

    return (
        <FormInputFieldWrapper
            {...props}
            type={type}
            focused={field.focused}
            isRequired={field.isRequired}
            onLabelClick={field.onLabelClick}
        >
            <select ref={field.inputRef} {...selectProps}>
                {options}
            </select>
        </FormInputFieldWrapper>
    );
};
